import {
  type ConditionalDisplayConfig,
  compareFieldCondition,
  FOLLOWUP_MODE_SOURCE,
  followupModeConditionValue,
  GOAL_RISK_SCORE_CAPPED_SOURCE,
  GOAL_TYPE_SOURCE,
  isProductAssessmentSource,
  isQuizResultSource,
  isQuizScoreSource,
  isScoreConditionSource,
  matchScoreCondition,
  PORTFOLIO_SCHEDULE_SOURCE,
  PORTFOLIO_TYPE_SOURCE,
  parseSeededScoreRange,
} from "@repo/shared";

/**
 * Page grouping for the public flow's three-level nav (§3.11). Called by both mode bodies.
 *
 * Only global pages are condition-filtered here, and they are evaluated with no goal and no
 * portfolio in context (QL-161: a global page's rules can only reference client/global sources),
 * exactly like the advisor wizard and the backend completion engine. Goal and portfolio pages are
 * collected by `pageType` alone — their rules are applied by the predicates below, in the layer
 * that knows the goal (and the portfolio). Filtering them here would resolve portfolio-scoped
 * sources against no portfolio and drop the page for *every* portfolio, before the narrowing
 * predicate ever runs.
 *
 * The `order` column is bucketed per (pageType, globalPosition, goalPosition) — multiple
 * pages can share an `order` value across buckets — so each bucket is sorted
 * independently and concatenated `[before … goal … after]`. QL-117: goal pages carry a
 * `goalPosition` (before / after the goal's portfolio pages); they stay one bucket here and the
 * shared goal-context view splits them around the portfolio dimension
 * (`splitGoalPagesByPosition`).
 */
export type PageBuckets = {
  globalPagesBefore: any[];
  goalPages: any[];
  portfolioPages: any[];
  globalPagesAfter: any[];
};

export function computePageBuckets(
  allPages: any[],
  formValues: Record<string, string>,
  goals: any[],
  fieldTypes?: Record<string, string>,
): PageBuckets {
  const byOrder = (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0);

  const globalPages = (position: string) =>
    allPages
      .filter(
        (p) =>
          p.pageType === "global" &&
          p.globalPosition === position &&
          evaluateCondition(p?.conditionalVisibility ?? null, { formValues, goals, fieldTypes }),
      )
      .sort(byOrder);

  return {
    globalPagesBefore: globalPages("before_goals"),
    goalPages: allPages.filter((p) => p.pageType === "goal").sort(byOrder),
    portfolioPages: allPages.filter((p) => p.pageType === "portfolio").sort(byOrder),
    globalPagesAfter: globalPages("after_goals"),
  };
}

/**
 * QL-120 — goal-page visibility, resolved against the goal the row is being drawn for. The
 * goal-scoped sources (`__goal_type__`, `__goal_risk_score__`, `__goal_has_risk__`,
 * `__goal_risk_score_capped__`) need that goal,
 * which `computePageBuckets` deliberately no longer supplies. Same shape as the advisor wizard's
 * predicate, handed down to GoalContextView.
 */
export function makeIsGoalPageVisible(
  formValues: Record<string, string>,
  goals: any[],
  fieldTypes?: Record<string, string>,
): (page: { conditionalVisibility?: any }, goalId: string) => boolean {
  return (page, goalId) =>
    evaluateCondition(page?.conditionalVisibility ?? null, {
      formValues,
      goals,
      fieldTypes,
      goalId,
    });
}

/**
 * QL-120 — portfolio-page visibility, resolved against the (goal × portfolio) pair the page is
 * being drawn for. The portfolio-scoped sources (`__portfolio_type__`,
 * `__portfolio_schedule_type__`) have no fallback (QPBI-321): without a portfolio in context they
 * resolve to the empty value, so the rule can only be evaluated inside GoalContextView, the one
 * layer that knows the active portfolio. Handed to that component as a predicate.
 */
export function makeIsPortfolioPageVisible(
  formValues: Record<string, string>,
  goals: any[],
  fieldTypes?: Record<string, string>,
): (page: { conditionalVisibility?: any }, goalId: string, portfolioId: string) => boolean {
  return (page, goalId, portfolioId) =>
    evaluateCondition(page?.conditionalVisibility ?? null, {
      formValues,
      goals,
      fieldTypes,
      goalId,
      portfolioId,
    });
}

/**
 * Data shape accepted by evaluateCondition().
 * Goal virtual fields are derived on-the-fly from the goals array —
 * no pre-synced formValues store needed (matches advisor flow pattern).
 */
export type PublicConditionEvaluationData = {
  formValues: Record<string, string>;
  goals: Array<{
    id: string;
    /** QP-150 — portfolioType/scheduleType live on goal_portfolio, ordered by portfolio order. */
    portfolios: Array<{
      id: string;
      portfolioType: string;
      scheduleType: string;
      /** QL-774 — server-derived follow-up lineage; feeds `__followup_mode__`. */
      isFollowup?: boolean;
    }>;
    riskScore: number | null;
    hasRiskAssessment: boolean;
    /** QL-120 — the goal's stored type (goal-type row id or GOAL_TYPE_NONE). */
    goalType: string;
    /**
     * QL-71 — server-derived `__goal_risk_score_capped__` state (one of
     * RISK_SCORE_CAP_STATES). Comes with the goal from `/public/flow/goals`, so
     * the public payload never carries the cap config or the client's field value.
     */
    riskScoreCapState: string;
  }>;
  /**
   * QPBI-395 — source field id → field type ("boolean" | "multiselect" | …).
   * Lets the rule comparison see only what the control renders. Keys are flow
   * field ids plus `__custom_field_<id>` sentinels for the client's custom
   * fields (their values ride in `formValues` under the same key). Virtual
   * sentinel sources (portfolio type/schedule, …) are absent by design and
   * keep the lenient string semantics.
   */
  fieldTypes?: Record<string, string>;
  goalId?: string | null;
  portfolioId?: string | null;
};

/**
 * Resolve a portfolio-scoped conditional source (QPBI-321). Requires the
 * current portfolio — portfolio attributes are never evaluated at goal/global
 * scope and there is NO fallback. Same semantics as the advisor evaluator and
 * the backend completion engine, keep the three in sync.
 */
function getPortfolioConditionField(
  goals: PublicConditionEvaluationData["goals"],
  goalId: string | null | undefined,
  portfolioId: string | null | undefined,
  field: "portfolioType" | "scheduleType",
): string {
  if (!goalId || !portfolioId) return "";
  const portfolios = goals.find((g) => g.id === goalId)?.portfolios ?? [];
  return portfolios.find((p) => p.id === portfolioId)?.[field] || "";
}

/**
 * Evaluate a conditional display rule.
 * Derives goal virtual fields on-the-fly from the goals array,
 * matching the advisor flow pattern in flow-wizard-utils.tsx.
 *
 * @param condition The conditional display configuration
 * @param data All data sources needed for evaluation
 * @returns true if the component should be shown, false if hidden
 */
export function evaluateCondition(
  condition: ConditionalDisplayConfig | null | undefined,
  data: PublicConditionEvaluationData,
): boolean {
  // No condition or disabled = always show
  if (!condition || !condition.enabled) {
    return true;
  }

  let fieldValue = "";
  const sourceFieldId = condition.sourceFieldId;

  // ── Quiz results (QL-13) — "unanswered" | "passed" | "failed" ──
  // Seeded into formValues under the sentinel key by use-public-flow-data,
  // the same way custom fields ride along. A quiz with no seeded value reads
  // as "unanswered", matching the advisor wizard and the backend engine.
  if (isQuizResultSource(sourceFieldId)) {
    fieldValue = data.formValues[sourceFieldId] || "unanswered";
  }
  // ── Product-assessment status / score and quiz score (QL-361) ──
  // Seeded by use-public-flow-data for every CONFIGURED source (and every quiz
  // on the flow), so a missing entry means the type, mode or quiz is not
  // there: such a rule never matches, whatever the operator — same early
  // return as the advisor wizard and the backend engine. A score is seeded ""
  // when there is none, which the shared verdict never matches either.
  else if (isProductAssessmentSource(sourceFieldId) || isQuizScoreSource(sourceFieldId)) {
    const seeded = data.formValues[sourceFieldId];
    if (seeded === undefined) return false;
    if (isScoreConditionSource(sourceFieldId)) {
      return matchScoreCondition(
        parseSeededScoreRange(seeded),
        condition.operator,
        condition.value || "",
      );
    }
    fieldValue = seeded;
  }
  // ── Session-level (QL-774): any follow-up portfolio makes it a follow-up ──
  else if (sourceFieldId === FOLLOWUP_MODE_SOURCE) {
    fieldValue = followupModeConditionValue(data.goals);
  }
  // ── Portfolio-scoped virtual fields (QPBI-321: portfolio pages only, no fallback) ──
  else if (sourceFieldId === PORTFOLIO_TYPE_SOURCE) {
    fieldValue = getPortfolioConditionField(
      data.goals,
      data.goalId,
      data.portfolioId,
      "portfolioType",
    );
  } else if (sourceFieldId === PORTFOLIO_SCHEDULE_SOURCE) {
    fieldValue = getPortfolioConditionField(
      data.goals,
      data.goalId,
      data.portfolioId,
      "scheduleType",
    );
  }
  // ── Goal-scoped virtual fields (resolved on-the-fly from goals array) ──
  else if (sourceFieldId === "__goal_has_risk__" && data.goalId) {
    fieldValue = data.goals.find((g) => g.id === data.goalId)?.hasRiskAssessment ? "true" : "false";
  } else if (sourceFieldId === "__goal_risk_score__" && data.goalId) {
    const score = data.goals.find((g) => g.id === data.goalId)?.riskScore;
    fieldValue = score != null ? String(score) : "";
  } else if (sourceFieldId === GOAL_TYPE_SOURCE && data.goalId) {
    // QL-120 — the goal's stored type; `in`/`not_in` rules test set membership.
    // No goal in context (global page) → "" → `in` hides, `not_in` shows.
    fieldValue = data.goals.find((g) => g.id === data.goalId)?.goalType ?? "";
  } else if (sourceFieldId === GOAL_RISK_SCORE_CAPPED_SOURCE && data.goalId) {
    // QL-71 — server-derived cap state; mirrored by the engine and flow-wizard-utils.tsx.
    // No goal in context (global page) → "" → `equals` hides, `not_equals` shows.
    fieldValue = data.goals.find((g) => g.id === data.goalId)?.riskScoreCapState ?? "";
  }
  // ── Regular field values (from formValues store) ──
  // Portfolio-scoped fields are keyed `${portfolioId}:${fieldId}`, goal-scoped
  // fields `${goalId}:${fieldId}`, global fields `${fieldId}`. Check most
  // specific first, fall back to less specific.
  else {
    const portfolioScopedKey = data.portfolioId ? `${data.portfolioId}:${sourceFieldId}` : null;
    const goalScopedKey = data.goalId ? `${data.goalId}:${sourceFieldId}` : null;
    fieldValue =
      (portfolioScopedKey && data.formValues[portfolioScopedKey] !== undefined
        ? data.formValues[portfolioScopedKey]
        : goalScopedKey && data.formValues[goalScopedKey] !== undefined
          ? data.formValues[goalScopedKey]
          : data.formValues[sourceFieldId]) || "";
  }

  return compareFieldCondition(
    fieldValue,
    condition.operator,
    condition.value || "",
    data.fieldTypes?.[sourceFieldId],
  );
}
